import React from 'react';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import Table from 'components/Tables/TableStructure/Table';
import Paper from 'components/Paper';
import searchAndSort from './functions/searchAndSort';
import SiteStatusIndicator from './components/SiteStatusIndicator';
import SGBody from 'components/Typography/SGBody';
import SGTitle from 'components/Typography/SGTitle';
import FilterDropdown from './FilterDropDown';
import {FilterOptions} from './interfaces/FilterOptions';
import ClearFilters from './components/ClearFilters';
import {useHistory} from 'react-router-dom';
import {Site} from 'hooks/useSiteInfo/interfaces/Site';

const StyledTable = styled(Table)`
  tr:hover td {
    background-color: ${props => props.theme.blue2}4D;
  }
  tr:active td {
    background-color: ${props => props.theme.blue2}80;
  }
`;

const StyledFilterContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const StyledStatusCell = styled.div`
  display: flex;
  align-items: center;
`;

const columns = [
  {
    id: 'siteCode',
    width: '2rem',
    label: 'NO/CODE',
  },
  {
    id: 'siteName',
    width: '8rem',
    label: 'Site Name',
  },
  {
    id: 'brand',
    width: '2rem',
    label: 'Brand ',
  },
  {
    id: 'geo',
    width: '2rem',
    label: 'geo',
  },
  {
    id: 'siteId',
    width: '2rem',
    label: 'Code',
  },
  {
    id: 'hasNpos',
    width: '2rem',
    label: 'NPOS',
    format: (value: boolean) => (
      <StyledStatusCell>
        <SGBody variant={'b4'}>{value ? 'Yes' : 'No'}</SGBody>
      </StyledStatusCell>
    ),
  },
  {
    id: 'businessStatus',
    width: '2rem',
    label: 'Status',
    format: (value: string) => <SiteStatusIndicator status={value} />,
  },
];

const renderFilters = (
  selectedFilters: Record<string, string>,
  handleFilterChange: (filterKey: string, value: string) => void,
  filledFilterOptions: FilterOptions,
  isDisabled: boolean
) => {
  const filterKeys = Object.keys(filledFilterOptions);
  return filterKeys.map(filterKey => {
    let filterKeyValues = Object.keys(filledFilterOptions[filterKey].values);
    let selectedValues = selectedFilters[filterKey] ?? '';
    return (
      <FilterDropdown
        isDisabled={isDisabled}
        filterKey={filterKey}
        label={filledFilterOptions[filterKey].label}
        selectedValue={selectedValues}
        options={filledFilterOptions[filterKey].values}
        handleFilterChange={handleFilterChange}
        filteredValueKeys={filterKeyValues}
      />
    );
  });
};

interface SiteListTableProps {
  sites: Site[];
  viewAllQuery: string | null;
  selectedFilters: Record<string, string>;
  handleFilterClear: () => void;
  handleFilterChange: (filterKey: string, value: string) => void;
  filterFilledOptions: FilterOptions;
}

function SiteListTable({
  sites,
  viewAllQuery,
  selectedFilters,
  handleFilterClear,
  handleFilterChange,
  filterFilledOptions,
}: SiteListTableProps) {
  const history = useHistory();
  const filteredSites = searchAndSort({
    filteredSearchValue: viewAllQuery || '',
    searchSuggestions: sites,
    filterOptions: selectedFilters,
  }).map(site => ({...site, disabled: !site.hasNpos}));

  const isClearable = Object.keys(selectedFilters).length > 0;
  const handleSiteClick = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    e.preventDefault();
    const siteId = e.currentTarget.getAttribute('clickkey');
    if (siteId !== null) {
      history.push(`/npos?siteId=${siteId}`);
    }
  };

  return (
    <Paper>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <SGTitle variant={'t2'} className={'text-color'}>
            Search Results For {viewAllQuery}
          </SGTitle>
        </Grid>
        <Grid item xs={12}>
          <StyledFilterContainer>
            {renderFilters(selectedFilters, handleFilterChange, filterFilledOptions, false)}
            {isClearable && <ClearFilters handleClearFilters={handleFilterClear} />}
          </StyledFilterContainer>
        </Grid>
        <Grid item xs={12}>
          <StyledTable
            columns={columns}
            data={filteredSites}
            loaded={true}
            paginationText="Sites"
            customRowsPerPage={{defaultRowsPerPage: 10}}
            clickAction={handleSiteClick}
            clickActionType="siteId"
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

export default SiteListTable;
